/**
 * Firebase — central SDK bootstrap (modular v10+).
 *
 * Additive integration: this sits ALONGSIDE the existing JWT + MongoDB
 * backend. iCaard's core auth, wallet, QR, and payments all continue to
 * flow through the FastAPI backend. Firebase is used for:
 *   • Auth (email/pw, Google, anonymous, phone) — optional side-door SSO
 *   • Firestore     — real-time state (chat, presence, live counters)
 *   • Storage       — customer-uploaded media (KYC docs, receipts)
 *   • Analytics     — page views (browser-only)
 *
 * Usage:
 *   import { auth, db, storage } from "@/lib/firebase";
 *   import { signInWithEmail } from "@/lib/firebase/auth";
 *   import { getDoc } from "@/lib/firebase/firestore";
 *   import { uploadFile } from "@/lib/firebase/storage";
 *
 * Every getter is lazy so we don't pay Firebase's cold-start cost on
 * routes that don't touch it.
 */
import { FirebaseApp, FirebaseOptions, getApps, initializeApp } from "firebase/app";
import { Analytics, getAnalytics, isSupported as analyticsSupported } from "firebase/analytics";
import { Auth, browserLocalPersistence, getAuth, setPersistence } from "firebase/auth";
import { Firestore, getFirestore } from "firebase/firestore";
import { FirebaseStorage, getStorage } from "firebase/storage";

const config: FirebaseOptions = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// ---------------------------------------------------------------------------
// Singletons — the modular SDK expects a single app per config; we guard
// against React StrictMode double-inits and hot-reload re-imports.
// ---------------------------------------------------------------------------
let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _analytics: Analytics | null = null;

function requireConfig(): void {
  const missing = Object.entries(config).filter(
    ([k, v]) => k !== "measurementId" && !v
  );
  if (missing.length) {
    throw new Error(
      `[firebase] Missing env vars: ${missing.map(([k]) => k).join(", ")}. ` +
        `Add them to /app/frontend/.env.local and restart the dev server.`
    );
  }
}

export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;
  requireConfig();
  _app = getApps()[0] ?? initializeApp(config);
  return _app;
}

/** Auth singleton. Uses browserLocalPersistence so sessions survive reloads. */
export function getFirebaseAuth(): Auth {
  if (_auth) return _auth;
  _auth = getAuth(getFirebaseApp());
  // Fire-and-forget: setPersistence returns a Promise but the auth object is
  // usable immediately; the promise only affects future writes.
  setPersistence(_auth, browserLocalPersistence).catch(() => {});
  return _auth;
}

export function getFirestoreDb(): Firestore {
  if (_db) return _db;
  _db = getFirestore(getFirebaseApp());
  return _db;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (_storage) return _storage;
  _storage = getStorage(getFirebaseApp());
  return _storage;
}

/** Analytics is browser-only + needs `measurementId`. Returns null when either
 *  precondition isn't met (e.g. Capacitor mobile shell, SSR, older browsers). */
export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (_analytics) return _analytics;
  if (!config.measurementId) return null;
  if (typeof window === "undefined") return null;
  try {
    if (!(await analyticsSupported())) return null;
    _analytics = getAnalytics(getFirebaseApp());
    return _analytics;
  } catch {
    return null;
  }
}

// Eager shortcuts — most callers just want `import { auth } from '@/lib/firebase'`.
export const auth = /*#__PURE__*/ new Proxy({} as Auth, {
  get: (_, prop) => (getFirebaseAuth() as any)[prop as string],
});
export const db = /*#__PURE__*/ new Proxy({} as Firestore, {
  get: (_, prop) => (getFirestoreDb() as any)[prop as string],
});
export const storage = /*#__PURE__*/ new Proxy({} as FirebaseStorage, {
  get: (_, prop) => (getFirebaseStorage() as any)[prop as string],
});

export { config as firebaseConfig };

/**
 * Verify the Firebase connection at app boot.
 *
 * Called by the diagnostic route `/app/dev/firebase-check`. Returns a
 * structured status object suitable for surfacing in the UI. Any thrown
 * errors are caught so the boot never fails just because Firebase is
 * misconfigured — the core JWT/backend flow is unaffected.
 */
export interface FirebaseHealth {
  ok: boolean;
  projectId?: string;
  authReady: boolean;
  firestoreReady: boolean;
  storageReady: boolean;
  analyticsReady: boolean;
  error?: string;
}
export async function verifyFirebaseConnection(): Promise<FirebaseHealth> {
  try {
    getFirebaseApp();
    const authInstance = getFirebaseAuth();
    const dbInstance = getFirestoreDb();
    const storageInstance = getFirebaseStorage();
    const analytics = await getFirebaseAnalytics();
    return {
      ok: true,
      projectId: config.projectId,
      authReady: !!authInstance,
      firestoreReady: !!dbInstance,
      storageReady: !!storageInstance,
      analyticsReady: !!analytics,
    };
  } catch (e: any) {
    return {
      ok: false,
      authReady: false,
      firestoreReady: false,
      storageReady: false,
      analyticsReady: false,
      error: e?.message || String(e),
    };
  }
}
